import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import "./CustomerCodes.css";

const CustomerCodes = () => {
  const [codes, setCodes] = useState([]);
  const [properties, setProperties] = useState([]); // List of properties
  const [newCode, setNewCode] = useState({
    siteName: "",
    code1: "",
    location1: "",
    code2: "",
    location2: "",
    code3: "",
    location3: "",
  });
  const [selectedCode, setSelectedCode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userRole, setUserRole] = useState(""); // Assuming you have a way to get the user's role
  const [searchTerm, setSearchTerm] = useState(''); // Search term
  const [sortOption, setSortOption] = useState(''); // Sort option
  const [filteredCodes, setFilteredCodes] = useState([]); // Filtered codes for search and sort

  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

        if (userDoc) {
          setUserRole(userDoc.data().role);
        }
      }
    };

    const fetchCodes = async () => {
      try {
        const codesRef = collection(db, "customerCodes");
        const querySnapshot = await getDocs(codesRef);
        const codesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCodes(codesList);
      } catch (error) {
        console.error("Error fetching codes:", error);
      }
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, "properties");
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProperties(propertiesList);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchUserData();
    fetchCodes();
    fetchProperties();
  }, []);

  useEffect(() => {
    filterAndSortCodes();
  }, [codes, searchTerm, sortOption]);

  // Sort properties alphabetically by propertyName
  const sortedProperties = properties.sort((a, b) => a.propertyName.localeCompare(b.propertyName));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCode((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "customerCodes"), newCode);
      setCodes([...codes, newCode]);
      setShowForm(false);
      setNewCode({
        siteName: "",
        code1: "",
        location1: "",
        code2: "",
        location2: "",
        code3: "",
        location3: "",
      });
    } catch (error) {
      console.error("Error adding code:", error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setNewCode(selectedCode);
  };

  const handleSave = async () => {
    try {
      const codeRef = doc(db, "customerCodes", selectedCode.id);
      await updateDoc(codeRef, newCode);
      setCodes(codes.map(code => code.id === selectedCode.id ? { ...code, ...newCode } : code));
      setSelectedCode({ ...selectedCode, ...newCode });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating code:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = async (id) => {
    if (userRole !== "Admin") {
      alert("You do not have permission to delete this code.");
      return;
    }
    try {
      await deleteDoc(doc(db, "customerCodes", id));
      setCodes(codes.filter(code => code.id !== id));
      setSelectedCode(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting code:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const filterAndSortCodes = () => {
    let filtered = codes.filter(code =>
      code.siteName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortOption === 'siteNameAsc') {
      filtered.sort((a, b) => a.siteName.localeCompare(b.siteName));
    } else if (sortOption === 'siteNameDesc') {
      filtered.sort((a, b) => b.siteName.localeCompare(a.siteName));
    }

    setFilteredCodes(filtered);
  };

  return (
    <div className="codes-container">
      <button className="blue-button" onClick={() => {
        setShowForm(true);
        setNewCode({
          siteName: "",
          code1: "",
          location1: "",
          code2: "",
          location2: "",
          code3: "",
          location3: "",
        });
      }}>Add Customer Code</button>
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add Customer Code</h3>
            <form className="form-container" onSubmit={handleSubmit}>
              <label>Property:</label>
              <select name="siteName" value={newCode.siteName} onChange={handleInputChange} required>
                <option value="">Select Property</option>
                {sortedProperties.map(property => (
                  <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                ))}
              </select>
              <label>Code 1:</label>
              <input type="text" name="code1" value={newCode.code1} onChange={handleInputChange} required />
              <label>Location 1:</label>
              <input type="text" name="location1" value={newCode.location1} onChange={handleInputChange} required />
              <label>Code 2:</label>
              <input type="text" name="code2" value={newCode.code2} onChange={handleInputChange} />
              <label>Location 2:</label>
              <input type="text" name="location2" value={newCode.location2} onChange={handleInputChange} />
              <label>Code 3:</label>
              <input type="text" name="code3" value={newCode.code3} onChange={handleInputChange} />
              <label>Location 3:</label>
              <input type="text" name="location3" value={newCode.location3} onChange={handleInputChange} />
              <div className="modal-buttons">
                <button type="submit" className="blue-button">Save</button>
                <button type="button" className="green-button" onClick={() => setShowForm(false)}>Close</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="search-sort-container">
        <input
          type="text"
          placeholder="Search by Property"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <select value={sortOption} onChange={handleSortChange}>
          <option value="">Sort By</option>
          <option value="siteNameAsc">Property (A-Z)</option>
          <option value="siteNameDesc">Property (Z-A)</option>
        </select>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Code 1</th>
              <th>Location 1</th>
              <th>Code 2</th>
              <th>Location 2</th>
              <th>Code 3</th>
              <th>Location 3</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCodes.map(code => (
              <tr key={code.id}>
                <td>{code.siteName}</td>
                <td>{code.code1}</td>
                <td>{code.location1}</td>
                <td>{code.code2}</td>
                <td>{code.location2}</td>
                <td>{code.code3}</td>
                <td>{code.location3}</td>
                <td>
                  <button className="blue-button" onClick={() => setSelectedCode(code)}>Details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCode && (
        <div className="modal-overlay">
          <div className="modal">
            {isEditing ? (
              <div className="form-container">
                <h3>Edit Code</h3>
                <label>Property:</label>
                <select name="siteName" value={newCode.siteName} onChange={handleInputChange} required>
                  <option value="">Select Property</option>
                  {sortedProperties.map(property => (
                    <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                  ))}
                </select>
                <label>Code 1:</label>
                <input type="text" name="code1" value={newCode.code1} onChange={handleInputChange} />
                <label>Location 1:</label>
                <input type="text" name="location1" value={newCode.location1} onChange={handleInputChange} />
                <label>Code 2:</label>
                <input type="text" name="code2" value={newCode.code2} onChange={handleInputChange} />
                <label>Location 2:</label>
                <input type="text" name="location2" value={newCode.location2} onChange={handleInputChange} />
                <label>Code 3:</label>
                <input type="text" name="code3" value={newCode.code3} onChange={handleInputChange} />
                <label>Location 3:</label>
                <input type="text" name="location3" value={newCode.location3} onChange={handleInputChange} />
                <div className="modal-buttons">
                  <button onClick={handleSave} className="blue-button">Save</button>
                  <button onClick={handleCancel} className="azul-button">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="popup-content">
                <h3>Code Details</h3>
                <p><strong>Property:</strong> {selectedCode.siteName}</p>
                <p><strong>Code 1:</strong> {selectedCode.code1}</p>
                <p><strong>Location 1:</strong> {selectedCode.location1}</p>
                <p><strong>Code 2:</strong> {selectedCode.code2}</p>
                <p><strong>Location 2:</strong> {selectedCode.location2}</p>
                <p><strong>Code 3:</strong> {selectedCode.code3}</p>
                <p><strong>Location 3:</strong> {selectedCode.location3}</p>
                <div className="modal-buttons">
                  <button onClick={handleEdit} className="blue-button">Edit</button>
                  {userRole === "Admin" && (
                    <button onClick={() => setShowDeleteConfirm(true)} className="red-button">Delete</button>
                  )}
                  <button onClick={() => setSelectedCode(null)} className="green-button">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Are you sure you want to delete?</h3>
            <div className="modal-buttons">
              <button onClick={() => handleDelete(selectedCode.id)} className="red-button">Yes</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="green-button">No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerCodes;